import { unreadable } from './shared.ts';

/**
 * `/hashtag/TAG` — a video list with a pager, deliberately not a search: the endpoint takes
 * no filters, which the page used to say in a sentence under the heading and no longer does —
 * it was a statement about an absence, and the absence is not what anyone arrives looking at.
 *
 * The empty sentence is the measured one. This endpoint answers an unused tag and an exhausted one
 * identically, so the page can only say which is more likely from the page number it was asked for,
 * and it says exactly that.
 */
export const hashtag = {
  loading: (tag: string): string => `Loading #${tag}…`,
  label: (tag: string): string => `#${tag}`,

  allDropped: {
    title: 'Could not read this hashtag',
    detail: 'The instance answered, and nothing in it could be shown.',
  },

  empty: {
    lastPageTitle: 'That was the last page',
    lastPageDetail: 'There is nothing further for this tag.',
    noneTitle: (tag: string): string => `Nothing tagged #${tag}`,
    noneDetail:
      'Either nobody has used this hashtag or YouTube would not serve it. The two look the same from here.',
  },

  problem: {
    notFound: {
      title: 'This instance has no hashtag pages',
      detail: 'They are not served here. Some instances leave parts of the app out.',
      retryable: false,
    },
    unreadableTitle: (tag: string): string => `Could not read #${tag}`,
    unreadable: unreadable(''),
  },

  paging: {
    backToPage: (page: number): string => `Back to page ${page}`,
    loadMore: (soFar: number): string => `Load more (${soFar} so far)`,
    loadingMore: 'Loading…',
    allLoaded: (count: number): string => `That is all ${count} videos under this tag.`,
  },
} as const;
